import BubbleChart from 'components/molecules/BubbleChart';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Switch } from 'antd';
import styled from '@emotion/styled';
import tw from 'twin.macro';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import { colorConstants } from 'config/colorConstants';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { opportunityIdentificationDownload } from 'parser/graphToExcelDownloadParser';
import { calculateContainerHeight } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    OPPORTUNITY_IDENTIFICATION_BUBBLE_COLOR_DROPDOWN_CHANGED,
    OPPORTUNITY_IDENTIFICATION_BUBBLE_SIZE_DROPDOWN_CHANGED,
    OPPORTUNITY_IDENTIFICATION_CHART_COPY,
    OPPORTUNITY_IDENTIFICATION_CHART_EXPORT,
    OPPORTUNITY_IDENTIFICATION_CHART_LEGEND_CLICKED,
    OPPORTUNITY_IDENTIFICATION_CHART_LEGEND_DROPDOWN_CLICKED,
    OPPORTUNITY_IDENTIFICATION_DETAILS_TOGGLED,
    OPPORTUNITY_IDENTIFICATION_X_AXIS_DROPDOWN_CHANGED,
    OPPORTUNITY_IDENTIFICATION_Y_AXIS_DROPDOWN_CHANGED,
} from 'utils/mixpanelEventNames';
import DownSvg from 'assets/svg/Down';
import { UserContext } from 'contexts/ConfigContext';
import { IDimension, IDropdownItem } from 'types/common';
import { FilterContextData } from 'contexts/FilterContext';
import { useFetchOpportunityBubbleChart } from 'hooks/opportunityIdentification/useFetchOpportunityBubbleChart';

const CustomSelect = styled(Select)`
    ${tw`border border-purpleFont rounded text-[13px] text-headerColor`}
    .ant-select-selector {
        height: 100% !important;
        box-shadow: none !important;
        border: none !important;
        display: flex;
        align-items: center;
    }
    .ant-select-selection-search-input {
        height: 100% !important;
    }
    .ant-select-arrow {
        ${tw`text-headerColor`}
    }
`;

const chartHeight = calculateContainerHeight(1, 16 * 13);

const OpportunityElasticityComparision = () => {
    const { appConfig: config } = useContext(UserContext);
    const { appConfig } = useContext(UserContext);
    const {
        allDimAndAttributeTypeList,
        idForRestOfComponent,
        timePeriodLoading,
        timePeriodSelection,
        currentFrequency,
        idForRestOfComponentLoading,
        filterNullError,
        allCheckedAttribute,
        timeSelectionLabel,
    } = useContext(FilterContextData);

    const {
        fetchOpportunityBubbleChart,
        data: bubbleData,
        loading: bubbleDataLoading,
        error: opportunityElasticityError,
    } = useFetchOpportunityBubbleChart();

    const [xAxis, setXAxis] = useState(appConfig.opportunityIdentification.kpis.xaxis[0]);
    const [yAxis, setYAxis] = useState(appConfig.opportunityIdentification.kpis.yaxis[0]);
    const [bubbleSize, setBubbleSize] = useState(appConfig.opportunityIdentification.kpis.bubbleSize[0]);
    const [bubbleColor, setBubbleColor] = useState(appConfig.opportunityIdentification.bubbleCategoryOptions[0]);
    const [showChartLabel, setShowChartLabel] = useState(true);
    const [bubbleSizeFilteredData, setBubbleFilteredData] = useState(getFilteredBubbleSizeData());

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !idForRestOfComponentLoading &&
            !timePeriodLoading &&
            bubbleColor
        ) {
            fetchOpportunityBubbleChart({
                variables: {
                    timePeriod: String(timePeriodSelection).toLowerCase(),
                    groupBy: bubbleColor.value,
                    frequency: currentFrequency?.key,
                    dimension: bubbleColor?.dimension,
                    filters: JSON.stringify(idForRestOfComponent),
                },
            });
        }
    }, [idForRestOfComponent, idForRestOfComponentLoading, allDimAndAttributeTypeList?.loading, timePeriodSelection]);

    useEffect(() => {
        setBubbleFilteredData(getFilteredBubbleSizeData());
    }, [bubbleSize, bubbleData]);

    let labelRef = useRef(showChartLabel);
    const refForBubble = useRef(null);

    const memoisedLabels = useMemo(() => {
        // add 5th index as name to show the details in bubble chart
        return [xAxis?.value, yAxis?.value, 'tactics', bubbleSize?.value, bubbleSize?.value, 'name'];
    }, [xAxis, yAxis, bubbleColor, bubbleSize]);

    // IF ERROR RETURN ERROR MESSAGE
    if (opportunityElasticityError && !bubbleDataLoading) return <ErrorBox msg={String(opportunityElasticityError)} />;

    // IF LOADING RETURN LOADER
    if (bubbleDataLoading)
        return (
            <div style={{ height: 700, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Loader />
            </div>
        );

    // filter out all the null values getting passed w.r.t selected bubbleSize
    function getFilteredBubbleSizeData() {
        const filteredData = bubbleData?.filter((item: any) => Math.abs(item[bubbleSize.value])) || [];
        return filteredData;
    }

    // groupBy change
    const onBubbleColorDropdownChange = (newBubbleColor: any) => {
        setBubbleColor(newBubbleColor);
        fetchOpportunityBubbleChart({
            variables: {
                timePeriod: String(timePeriodSelection).toLowerCase(),
                groupBy: newBubbleColor.value,
                frequency: currentFrequency?.key,
                dimension: newBubbleColor?.dimension,
                filters: JSON.stringify(idForRestOfComponent),
            },
        });
    };

    return (
        <div>
            <div className="flex justify-between mt-4">
                <div className="flex gap-4">
                    <div className="flex gap-2 items-center">
                        <span className="font-xs">Bubble Color</span>
                        <CustomSelect
                            suffixIcon={<DownSvg />}
                            className="dropdown-height dropdown-width uniqSelOIBubbleDropdown"
                            value={bubbleColor?.value}
                            options={config.opportunityIdentification.bubbleCategoryOptions}
                            onChange={(val: any, item: any) => {
                                onBubbleColorDropdownChange(item);
                                //mixpanel event
                                trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_BUBBLE_COLOR_DROPDOWN_CHANGED, {
                                    bubbleColorName: item?.label,
                                });
                            }}
                        />
                    </div>
                </div>
                <div className="flex gap-4">
                    <div className="flex gap-2 items-center">
                        <span className="font-xs">X Axis</span>
                        <CustomSelect
                            suffixIcon={<DownSvg />}
                            className="dropdown-height dropdown-width uniqSelOIXAxisDropdown"
                            value={xAxis?.value}
                            options={config.opportunityIdentification.kpis.xaxis?.filter(
                                (option: any) => ![yAxis.value, bubbleSize.value].includes(option.value),
                            )}
                            onChange={(val: any, item: any) => {
                                setXAxis(item);
                                //mixpanel event
                                trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_X_AXIS_DROPDOWN_CHANGED, {
                                    xAxisColorName: item?.label,
                                });
                            }}
                        />
                    </div>
                    <div className="flex gap-2 items-center">
                        <span className="font-xs">Y Axis</span>
                        <CustomSelect
                            suffixIcon={<DownSvg />}
                            className="dropdown-height dropdown-width uniqSelOIYAxisDropdown"
                            value={yAxis?.value}
                            options={config.opportunityIdentification.kpis.yaxis?.filter(
                                (option: any) => ![xAxis.value, bubbleSize.value].includes(option.value),
                            )}
                            onChange={(val: any, item: any) => {
                                setYAxis(item);
                                //mixpanel event
                                trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_Y_AXIS_DROPDOWN_CHANGED, {
                                    yAxisColorName: item?.label,
                                });
                            }}
                        />
                    </div>
                    <div className="flex gap-2 items-center">
                        <span className="font-xs">Bubble Size</span>
                        <CustomSelect
                            suffixIcon={<DownSvg />}
                            className="dropdown-height dropdown-width uniqSelOIBubbleSizeDropdown"
                            value={bubbleSize?.value}
                            options={config.opportunityIdentification.kpis.bubbleSize?.filter(
                                (option: any) => ![yAxis.value, xAxis.value].includes(option.value),
                            )}
                            onChange={(val: any, item: any) => {
                                setBubbleSize(item);
                                //mixpanel event
                                trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_BUBBLE_SIZE_DROPDOWN_CHANGED, {
                                    bubbleSizeColorName: item?.label,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="border border-borderLine rounded-sm px-4 py-2 mt-4" ref={refForBubble}>
                <div className="flex justify-between items-center">
                    <span className="font-lg font-medium">
                        Quadrant Chart : {xAxis?.label} vs {yAxis?.label}
                    </span>
                    <div className="flex items-center gap-4">
                        <div className="flex gap-2 items-center mr-[3px]">
                            <span className="font-xs">Details</span>
                            <Switch
                                style={{
                                    backgroundColor: showChartLabel ? colorConstants.violet : colorConstants.borderGrey,
                                }}
                                defaultChecked
                                onChange={() => {
                                    setShowChartLabel(!showChartLabel);
                                    //mixpanel event
                                    trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_DETAILS_TOGGLED, {
                                        isSelected: !showChartLabel,
                                    });
                                }}
                                checked={showChartLabel}
                                className="uniqSelOIDetailsSwitch"
                            />
                        </div>
                        <div className="flex gap-2">
                            <CopyToClipBoard
                                mixpanelEvent={() => trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_CHART_COPY)}
                                refOfComponent={refForBubble}
                            />
                            <DownloadExcel
                                mixpanelEvent={() => trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_CHART_EXPORT)}
                                excelData={opportunityIdentificationDownload(
                                    bubbleSizeFilteredData,
                                    appConfig.opportunityIdentification,
                                )}
                                headers={[
                                    `Bubble Color (${bubbleColor?.label?.toLowerCase()})`,
                                    'BPE',
                                    'PPE',
                                    'MAC',
                                    'MAC %',
                                    'RSV',
                                    'RSV Growth %',
                                    'Eq Volume',
                                ]}
                                title={'OpportunityIdentification'}
                            />
                        </div>
                    </div>
                </div>
                <div className="w-full py-3 mb-1 overflow-auto"></div>
                <div>
                    <>
                        <div className="pr-5 mt-[-80px] relative top-[138px] mb-2 font-xs font-medium text-greyFont mb-6">
                            {yAxis?.label}
                        </div>
                        <BubbleChart
                            mixpanelTrackLegend={(text: string | undefined) => {
                                //if text present, then trigger mixpanel api for legend , else trigger for legend dropdown
                                if (text) {
                                    trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_CHART_LEGEND_CLICKED, {
                                        legendItem: text,
                                    });
                                } else {
                                    trackEventMixpanel(OPPORTUNITY_IDENTIFICATION_CHART_LEGEND_DROPDOWN_CLICKED);
                                }
                            }}
                            chartContainer={'bubble-opportunity-identification'}
                            height={chartHeight}
                            dataset={bubbleSizeFilteredData || []}
                            k={showChartLabel}
                            labelConfiguration={showChartLabel}
                            yAxisTitle={yAxis?.label}
                            xAsisTitle={xAxis?.label}
                            bubbleSizeTitle={bubbleSize?.label}
                            showQuadrant={true}
                            fields={memoisedLabels}
                            alter={false}
                            lineColor={colorConstants.borderGrey}
                            fill={'#171E3E'}
                            stroke={'#d9d9d9'}
                            loading={bubbleDataLoading}
                            error={null}
                        />
                    </>
                </div>
            </div>
        </div>
    );
};

export default OpportunityElasticityComparision;
